import { useState, useEffect } from 'react';
import RichTextEditor from '../../../components/RichTextEditor';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchCourseQuestions(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_self_evaluation_questions?course_id=eq.${courseId}&select=*&order=section.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
  return response.json();
}

async function upsertCourseQuestion(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_self_evaluation_questions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

const SECTIONS = ['Mid-Semester', 'Final'];

export default function CourseSelfEvaluation({ course }) {
  const [questions, setQuestions] = useState({});
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCourseQuestions(course.id)
      .then(data => {
        const map = {};
        data.forEach(q => { map[q.section] = q; });
        setQuestions(map);
        const editMap = {};
        SECTIONS.forEach(section => {
          editMap[section] = {
            section_title: map[section]?.section_title || '',
            section_description: map[section]?.section_description || '',
            prompt: map[section]?.prompt || '',
            required: map[section]?.required !== false
          };
        });
        setEditValues(editMap);
      })
      .finally(() => setLoading(false));
  }, [course.id]);

  async function handleSave(section) {
    setError('');
    setSuccess('');
    const values = editValues[section];
    if (!values.section_title.trim()) return setError('Enter a section heading.');
    if (!values.prompt.trim()) return setError('Enter the question prompt.');

    setSaving(prev => ({ ...prev, [section]: true }));
    try {
      const result = await upsertCourseQuestion({
        course_id: course.id,
        section,
        section_title: values.section_title.trim(),
        section_description: values.section_description,
        prompt: values.prompt.trim(),
        required: values.required,
        updated_at: new Date().toISOString()
      });
      setQuestions(prev => ({ ...prev, [section]: result[0] }));
      setSuccess(`${section} question saved.`);
    } catch (err) {
      setError('Failed to save question.');
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <h2 style={styles.subheading}>Self-Evaluation Questions</h2>
      <p style={styles.muted}>
        Each evaluation period has one course-specific question that appears alongside
        the shared question set. Set the section heading, an optional description,
        and the question prompt for each.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {SECTIONS.map(section => {
        const values = editValues[section] || {};
        const isSaving = saving[section] || false;
        const existing = questions[section];

        return (
          <div key={section} style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionHeading}>{section}</h3>
              {existing && <span style={styles.pill}>Configured</span>}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Section heading</label>
              <input
                style={styles.input}
                type="text"
                value={values.section_title || ''}
                onChange={e => setEditValues(prev => ({
                  ...prev,
                  [section]: { ...prev[section], section_title: e.target.value }
                }))}
                placeholder="e.g. Philosophy of Religion"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Section description (optional)</label>
              <RichTextEditor
                value={values.section_description || ''}
                onChange={val => setEditValues(prev => ({
                  ...prev,
                  [section]: { ...prev[section], section_description: val }
                }))}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Question prompt</label>
              <textarea
                style={styles.textarea}
                value={values.prompt || ''}
                onChange={e => setEditValues(prev => ({
                  ...prev,
                  [section]: { ...prev[section], prompt: e.target.value }
                }))}
                placeholder="Enter the course-specific question..."
                rows={3}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={values.required !== false}
                  onChange={e => setEditValues(prev => ({
                    ...prev,
                    [section]: { ...prev[section], required: e.target.checked }
                  }))}
                  style={{ marginRight: '8px' }}
                />
                Required
              </label>
            </div>

            <button
              style={styles.btn}
              disabled={isSaving}
              onClick={() => handleSave(section)}
            >
              {isSaving ? 'Saving...' : `Save ${section} Question`}
            </button>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  sectionCard: {
    border: '1px solid #d7dee5', borderRadius: '10px', padding: '20px',
    background: 'white', marginBottom: '16px'
  },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px'
  },
  sectionHeading: { margin: '0', fontSize: '16px', color: '#1f2933' },
  pill: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  textarea: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box',
    background: 'white', resize: 'vertical'
  },
  checkLabel: {
    fontSize: '14px', color: '#1f2933', display: 'flex',
    alignItems: 'center', cursor: 'pointer'
  },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  }
};